import { useCallback, useEffect, useState } from 'react';
import { Box, Text, Alert, AlertIcon, AlertTitle, ListItem, OrderedList } from '@chakra-ui/react';

import { ImmigrationStatusShareCodeDoc } from 'types';
import { getDateFromString, getStringFromDate } from '../../../../utilities/helpers';
import { IDate, IQuestion } from '../../../../types';
import { Modal } from '../../../atoms/modal';
import { DateInputWithConfirm } from '../../../formInputs/DateInput';
import { InputWithConfirm } from '../../../formInputs/Input';
import { RadioOptionsWithConfirm } from '../../../formInputs/RadioOptions';
import { TextareaWithConfirm } from '../../../formInputs/Textarea';
import UploadActions from '../../../atoms/upload-actions';
import { validateDate } from '../../../../utilities/validateDate';
import { Link } from '../../../atoms/link';

enum SelectionOptions {
  YES = 'Yes',
  NOT_YET = 'Not yet',
}

enum GenerateSharCodeTodayOptions {
  YES = 'Yes',
  NO = 'No',
}

enum ImmigrationStatus {
  STUDENT = 'Student',
  SETTLED = 'Settled',
  PRE_SETTLED = 'Pre-settled',
  OTHER = 'Other',
}

export interface IProps {
  title: string;
  closeModal: () => void;
  docKey: string;
  shareCode: ImmigrationStatusShareCodeDoc;
  onSave: (docKey: string, data: any) => void;
}

export const ImmigrationStatusShareCodeModal = (props: IProps) => {
  const { title, closeModal, onSave, docKey, shareCode: shareCodeDoc } = props;

  const isComplete = !!shareCodeDoc?.shareCode?.length && !!shareCodeDoc?.shareCodeGenerateDate?.length;

  const [isChanged, setIsChanged] = useState<boolean>(false);
  const [isDecisionReceived, setIsDecisionReceived] = useState<SelectionOptions | undefined>(
    isComplete ? SelectionOptions.YES : undefined,
  );
  const [immigrationStatus, setImmigrationStatus] = useState<ImmigrationStatus>(
    (shareCodeDoc?.immigrationStatus ?? '') as ImmigrationStatus,
  );
  const [shareCode, setShareCode] = useState(shareCodeDoc?.shareCode ?? '');
  const [generateSharCodeToday, setGenerateSharCodeToday] = useState<undefined | boolean>(undefined);
  const [shareCodeGenerateDate, setShareCodeGenerateDate] = useState(
    getDateFromString(shareCodeDoc.shareCodeGenerateDate),
  );
  const [shareCodeGenerateDateError, setShareCodeGenerateDateError] = useState('');

  const [startDate, setStartDate] = useState(getDateFromString(shareCodeDoc.startDate));
  const [startDateError, setStartDateError] = useState('');

  const [untilDate, setUntilDate] = useState(getDateFromString(shareCodeDoc.validUntil));
  const [untilDateError, setUntilDateError] = useState('');

  const [detailsOfStatusGranted, setDetailsOfStatusGranted] = useState(shareCodeDoc?.detailsOfStatusGranted ?? '');

  const [shareCodeErrMsg, setShareCodeErrMsg] = useState<string | null>(null);

  useEffect(() => {
    if (isChanged) {
      setStartDate({ DD: '', MM: '', YYYY: '' });
      setStartDateError('');

      setUntilDate({ DD: '', MM: '', YYYY: '' });
      setUntilDateError('');

      setDetailsOfStatusGranted('');
    }
  }, [immigrationStatus]);

  useEffect(() => {
    // validate share code

    // 1: Share code Format Validation (start with "S")
    // 2: Share code Length Validation (9 characters long)
    // 3: Share code Pattern Validation (starts with "S" and the remaining 8 characters should be numnbers or letters)
    const pattern = /^[Ss][a-zA-Z0-9]{8}$/;
    const isPatternMatched = pattern.test(shareCode);
    if (!isPatternMatched) {
      setShareCodeErrMsg(
        'Share code should start with "S" and the remaining 8 characters should be numbers or letters',
      );
    } else {
      setShareCodeErrMsg(null);
    }
  }, [shareCode]);

  const handleQuestionsClick = (question: IQuestion) => {
    if (question.label !== isDecisionReceived) {
      setIsDecisionReceived(question.label as any);
      setIsChanged(true);
    }
  };

  const handleImmigrationsStatusClick = (question: IQuestion) => {
    if (question.label !== immigrationStatus) {
      setImmigrationStatus(question.label as any);
      setIsChanged(true);
    }
  };

  const handleGenerateSharCodeDateClick = (question: IQuestion) => {
    if ((question.label === GenerateSharCodeTodayOptions.YES) !== generateSharCodeToday) {
      setGenerateSharCodeToday(question.label === GenerateSharCodeTodayOptions.YES);
      setIsChanged(true);
    }
  };
  const returnToSection = () => {
    closeModal();
  };

  const checkDate = useCallback((date: IDate) => {
    return date.DD?.length && date.MM?.length && date.YYYY?.length === 4;
  }, []);

  const calculateIsComplete = () => {
    let status = !!shareCode.length && !!immigrationStatus.length;

    if (!generateSharCodeToday) {
      status = status && Boolean(checkDate(shareCodeGenerateDate)) && !shareCodeGenerateDateError;
    }

    if (immigrationStatus === ImmigrationStatus.STUDENT) {
      status = status && Boolean(checkDate(startDate)) && !startDateError;
    }
    if ([ImmigrationStatus.STUDENT, ImmigrationStatus.PRE_SETTLED].includes(immigrationStatus)) {
      status = status && Boolean(checkDate(untilDate)) && !untilDateError;
    }
    if (immigrationStatus === ImmigrationStatus.OTHER) {
      status = status && !!detailsOfStatusGranted.length;
    }

    return status && shareCodeErrMsg === null;
  };

  const save = () => {
    let dateToSave: IDate = shareCodeGenerateDate;
    if (generateSharCodeToday) {
      const today = new Date();
      dateToSave = {
        DD: today.getDate().toString(),
        MM: (today.getMonth() + 1).toString(),
        YYYY: today.getFullYear().toString(),
      };
    }
    const data: any = {
      shareCode,
      shareCodeGenerateDate: getStringFromDate(dateToSave),
      immigrationStatus,
      startDate: getStringFromDate(startDate),
      validUntil: getStringFromDate(untilDate),
      detailsOfStatusGranted,
    };

    onSave(docKey, data);
  };

  return (
    <Modal id={'immigrationStatusShareCode'} show={true} title={title} onClose={closeModal} hideFooter>
      <Text data-selector={'intro-text'}>
        To complete this task you need to access your eVisa using your UKVI account. If you do not have an account, or
        for more information about eVisas, please check{' '}
        <Link href="https://www.gov.uk/evisa" isExternal>
          eVisas: access and use your online immigration status
        </Link>
        .
      </Text>

      <div data-selector="isDecisionReceived-label">
        <RadioOptionsWithConfirm
          key={'isDecisionReceived'}
          value={isDecisionReceived}
          label={'Have you already received a decision on your eVisa application to study in the UK?'}
          onClick={handleQuestionsClick}
          isInline={true}
          maxQuestions={2}
          questions={[
            {
              char: 'Y',
              label: SelectionOptions.YES,
              selected: isDecisionReceived === SelectionOptions.YES,
            },
            {
              char: 'N',
              label: SelectionOptions.NOT_YET,
              selected: isDecisionReceived === SelectionOptions.NOT_YET,
            },
          ]}
        />
      </div>

      {isDecisionReceived === SelectionOptions.YES ? (
        <>
          <Alert status="info" data-selector="create-share-code-link-wrapper" mb={6}>
            <AlertIcon />

            <Box>
              <AlertTitle>Get a share code to prove your immigration status</AlertTitle>

              <OrderedList>
                <ListItem>
                  Follow the instructions on{' '}
                  <Link href={'https://www.gov.uk/evisa/view-evisa-get-share-code-prove-immigration-status'} isExternal>
                    eVisas: access and use your online immigration status
                  </Link>
                </ListItem>
                <ListItem>
                  Click the button for <em>View your eVisa and get a share code</em>
                </ListItem>
                <ListItem>
                  Continue by using the option <em>To prove my immigration status for anything else</em>
                </ListItem>
                <ListItem>
                  Use the button <em>Create a share code</em>
                </ListItem>
                <ListItem>Copy the share code beginning with S (e.g. S1ABCDEFG)</ListItem>
              </OrderedList>

              <Text mb={0}>
                These instructions are provided as a guideline only as references to specific labels may change in the
                future. Always follow the latest guidance on{' '}
                <Link href={'https://www.gov.uk/evisa/view-evisa-get-share-code-prove-immigration-status'} isExternal>
                  eVisas: access and use your online immigration status
                </Link>
                .
              </Text>
              <Text>
                <b>You will need to return to this page to enter the share code below.</b>
              </Text>
            </Box>
          </Alert>

          <InputWithConfirm
            label="Please enter your share code from gov.uk"
            subText="Your share code proving your right to study begins with S and is followed by 8 numbers or letters."
            value={shareCode}
            placeholder="e.g. S1ABCDEFG"
            onChange={(evt: React.FormEvent<HTMLInputElement>) => {
              setShareCode((evt?.currentTarget?.value ?? '').trim());
              setIsChanged(true);
            }}
            errorText={shareCodeErrMsg}
          />

          {!isComplete && (
            <div data-selector="generateSharCodeToday-questions">
              <RadioOptionsWithConfirm
                label={'Did you generate this share code today? '}
                id="generateSharCodeToday"
                key="generateSharCodeToday"
                onClick={handleGenerateSharCodeDateClick}
                isInline={true}
                questions={[
                  {
                    char: 'Y',
                    label: GenerateSharCodeTodayOptions.YES,
                    selected: generateSharCodeToday,
                  },
                  {
                    char: 'N',
                    label: GenerateSharCodeTodayOptions.NO,
                    selected: generateSharCodeToday === false,
                  },
                ]}
              />
            </div>
          )}

          {(generateSharCodeToday === false || isComplete) && (
            <DateInputWithConfirm
              id={`shareCode-generate-date`}
              label={`When did you generate this share code?`}
              value={shareCodeGenerateDate}
              errorText={shareCodeGenerateDateError}
              onChangeDD={(evt: any) => {
                setShareCodeGenerateDate({
                  ...shareCodeGenerateDate,
                  DD: evt.target.value,
                });
                setIsChanged(true);
              }}
              onChangeMM={(evt: any) => {
                setShareCodeGenerateDate({
                  ...shareCodeGenerateDate,
                  MM: evt.target.value,
                });
                setIsChanged(true);
              }}
              onChangeYY={(evt: any) => {
                setShareCodeGenerateDate({
                  ...shareCodeGenerateDate,
                  YYYY: evt.target.value,
                });
                setIsChanged(true);
              }}
              onBlur={() => setShareCodeGenerateDateError(validateDate(shareCodeGenerateDate))}
            />
          )}

          <RadioOptionsWithConfirm
            label={'Which Immigration status have you been granted?'}
            key="immigrationStatus"
            id="immigrationStatus"
            onClick={handleImmigrationsStatusClick}
            isInline={false}
            questions={[
              {
                char: '1',
                label: ImmigrationStatus.STUDENT,
                selected: immigrationStatus === ImmigrationStatus.STUDENT,
              },
              {
                char: '2',
                label: ImmigrationStatus.SETTLED,
                selected: immigrationStatus === ImmigrationStatus.SETTLED,
              },
              {
                char: '3',
                label: ImmigrationStatus.PRE_SETTLED,
                selected: immigrationStatus === ImmigrationStatus.PRE_SETTLED,
              },
              {
                char: '4',
                label: ImmigrationStatus.OTHER,
                selected: immigrationStatus === ImmigrationStatus.OTHER,
              },
            ]}
          />

          {immigrationStatus === ImmigrationStatus.STUDENT && (
            <DateInputWithConfirm
              id={`immigrationStatus-start-date`}
              label={`What is the valid from date for this immigration status?`}
              value={startDate}
              errorText={startDateError}
              onChangeDD={(evt: any) => {
                setStartDate({
                  ...startDate,
                  DD: evt.target.value,
                });
                setIsChanged(true);
              }}
              onChangeMM={(evt: any) => {
                setStartDate({
                  ...startDate,
                  MM: evt.target.value,
                });
                setIsChanged(true);
              }}
              onChangeYY={(evt: any) => {
                setStartDate({
                  ...startDate,
                  YYYY: evt.target.value,
                });
                setIsChanged(true);
              }}
              onBlur={() => setStartDateError(validateDate(startDate))}
            />
          )}

          {[ImmigrationStatus.STUDENT, ImmigrationStatus.PRE_SETTLED].includes(immigrationStatus) && (
            <DateInputWithConfirm
              id={`immigrationStatus-valid-until`}
              label={`What is the valid until date for this immigration status?`}
              value={untilDate}
              errorText={untilDateError}
              onChangeDD={(evt: any) => {
                setUntilDate({
                  ...untilDate,
                  DD: evt.target.value,
                });
                setIsChanged(true);
              }}
              onChangeMM={(evt: any) => {
                setUntilDate({
                  ...untilDate,
                  MM: evt.target.value,
                });
                setIsChanged(true);
              }}
              onChangeYY={(evt: any) => {
                setUntilDate({
                  ...untilDate,
                  YYYY: evt.target.value,
                });
                setIsChanged(true);
              }}
              onBlur={() => setUntilDateError(validateDate(untilDate))}
            />
          )}

          {immigrationStatus === ImmigrationStatus.OTHER && (
            <TextareaWithConfirm
              id={'immigration-status-details'}
              label={
                'Please enter the status granted as well as any start or valid until dates for this status (if available)'
              }
              onChange={(evt: any) => {
                setDetailsOfStatusGranted(evt.currentTarget.value);
                setIsChanged(true);
              }}
              value={detailsOfStatusGranted}
            />
          )}

          {isChanged ? (
            <div data-selector="yes-button">
              <UploadActions complete={true} label={undefined} disabled={!calculateIsComplete()} onContinue={save} />
            </div>
          ) : (
            <div>
              <UploadActions complete={false} label={undefined} disabled={false} onContinue={returnToSection} />
            </div>
          )}
        </>
      ) : (
        <>
          <Alert status="info" data-selector="not-yet-wrapper" mb={6}>
            If you have not received the decision on your application yet, please come back to this page once you have
            received it.
          </Alert>

          <div data-selector="not-yet-button">
            <UploadActions complete={false} label={undefined} disabled={false} onContinue={returnToSection} />
          </div>
        </>
      )}
    </Modal>
  );
};
